import createHttp from '@/config/http';
import type { Invoice, InvoiceItem, InvoiceResponse } from '@/types/invoice';
import type { ResponseMetaData } from '@/types/service';

// localhost:3003
// Use full URL from environment variable
const http = createHttp(import.meta.env.VITE_INVOICE_URL);

export const getInvoices = async (
	page = 1,
	limit = 15,
	tab = '',
	columnsFilters = '[]',
	search = '',
	status = '',
	globalFilters = '[]',
	sort = '',
	dateRangeFilter = '',
	additionalFilters = '',
): Promise<{
	items: Invoice[];
	meta: ResponseMetaData;
}> => {
	const response = await http.get<InvoiceResponse>('/invoices/db', {
		params: {
			tab,
			page,
			limit,
			columnsFilters,
			search,
			status,
			globalFilters,
			sort,
			dateRangeFilter,
			additionalFilters,
		},
	});
	return {
		items: response.data.data.items,
		meta: response.data.data.metaData,
	};
};

export const getInvoicesProducts = async (
	page = 1,
	limit = 15,
	globalFilters = '[]',
): Promise<{
	items: { productName: string; qCode: string }[];
	totalRecords: number;
}> => {
	const response = await http.get('/invoices/db/products', {
		params: {
			page,
			limit,
			globalFilters,
		},
	});
	return {
		items: response.data.items,
		totalRecords: response.data.totalRecords,
	};
};

//Get the PDF of each invoice using the Invoice Number
export const getInvoicePDF = async (invoiceNumberData: string) => {
	const response = await http.get(
		`invoices/download-invoice/${invoiceNumberData}`,
		{
			responseType: 'arraybuffer',
		},
	);

	return response.data;
};


export const getRecentPayments = async (page: number, limit: number, clinics: string[]) => {
	const response = await http.get('/stripe/payments-list', {
		params: {
			page,
			limit,
			clinics: JSON.stringify(clinics),
		},
	});
	return response.data.data.items;
}

export const getRecentInvoices = async (page: number, limit: number, clinics: string[]): Promise<{
	items: InvoiceItem[];
	meta: ResponseMetaData;
	overDueInvoicesCount: number;
	paidInvoicesCount: number;
}> => {
	const response = await http.get('/invoices/latest', {
		params: {
			page,
			limit,
			clinicIds: clinics.join(','),
		},
	});
	return {
		items: response.data.data.items,
		meta: response.data.data.metaData,
		overDueInvoicesCount: response.data.data.overDueInvoicesCount,
		paidInvoicesCount: response.data.data.paidInvoicesCount,
	};
}

export const getInvoiceScoreCards = async (page: number, limit: number, clinics: string[], dateRangeFilter?: string): Promise<{
	items: InvoiceItem[];
	meta: ResponseMetaData;
	overDueInvoicesCount: number;
	paidInvoicesCount: number;
}> => {
	const response = await http.get('/invoices/score-cards', {
		params: {
			page,
			limit,
			clinicIds: clinics.join(','),
			dateRangeFilter: dateRangeFilter || '',
		},
	});
	return {
		items: response.data.data.items,
		meta: response.data.data.metaData,
		overDueInvoicesCount: response.data.data.overDueInvoicesCount,
		paidInvoicesCount: response.data.data.paidInvoicesCount,
	};
}

export const getOverDueAmount = async (page: number, limit: number, clinics: string[], dateRangeFilter?: string): Promise<{
	amountDue: number;
	amountPaid: number;
	meta: ResponseMetaData;
}> => {
	const response = await http.get('/invoices/get-invoices-amount', {
		params: {
			page,
			limit,
			clinicIds: clinics.join(','),
			dateRangeFilter: dateRangeFilter || '',
		},
	});
	return {
		amountDue: response.data.data.amountDue,
		amountPaid: response.data.data.amountPaid,
		meta: response.data.data.metaData,
	};
}